import { useState, useEffect } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useVoiceInput } from '@/hooks/use-voice-input';
import { motion, AnimatePresence } from 'framer-motion';

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
  onListeningChange?: (isListening: boolean) => void;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function VoiceInputButton({
  onTranscript,
  onListeningChange,
  disabled = false,
  className,
  size = 'md',
}: VoiceInputButtonProps) {
  const [showUnsupported, setShowUnsupported] = useState(false);

  const {
    isListening,
    isSupported,
    transcript,
    error,
    startListening,
    stopListening,
  } = useVoiceInput({
    onResult: (text) => {
      onTranscript(text);
    },
    onError: (err) => {
      console.error('Voice error:', err);
    },
  });

  // Notify parent of listening state changes
  useEffect(() => {
    onListeningChange?.(isListening);
  }, [isListening, onListeningChange]);

  // Show unsupported message briefly
  useEffect(() => {
    if (!isSupported && showUnsupported) {
      const timer = setTimeout(() => setShowUnsupported(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isSupported, showUnsupported]);

  const handlePress = () => {
    if (!isSupported) {
      setShowUnsupported(true);
      return;
    }

    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  return (
    <div className="relative">
      <motion.button
        type="button"
        onClick={handlePress}
        disabled={disabled}
        whileTap={{ scale: 0.95 }}
        className={cn(
          'relative flex items-center justify-center rounded-full transition-all touch-target',
          sizeClasses[size],
          isListening
            ? 'bg-destructive text-destructive-foreground shadow-lg shadow-destructive/30'
            : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        aria-label={isListening ? 'Stop listening' : 'Start voice input'}
      >
        <AnimatePresence mode="wait">
          {isListening ? (
            <motion.div
              key="listening"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="relative"
            >
              <MicOff className={iconSizes[size]} />
              {/* Pulsing ring animation */}
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-destructive-foreground"
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [1, 0, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
            >
              <Mic className={iconSizes[size]} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Listening indicator */}
      <AnimatePresence>
        {isListening && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap"
          >
            <div className="flex items-center gap-1.5 px-2 py-1 bg-destructive text-destructive-foreground text-xs rounded-full">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Listening...</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Live transcript preview */}
      <AnimatePresence>
        {isListening && transcript && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-8 w-48 max-w-xs"
          >
            <div className="px-3 py-2 bg-popover border rounded-lg shadow-lg text-sm text-popover-foreground truncate">
              {transcript}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error/unsupported message */}
      <AnimatePresence>
        {(error || showUnsupported) && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48"
          >
            <div className="px-3 py-2 bg-destructive/10 border border-destructive/20 rounded-lg text-xs text-destructive text-center">
              {error || 'Voice input not supported in this browser'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
